import { useState } from "react";

export const UNITS = ["g", "kg", "oz", "lb", "mL", "L", "cups", "tbsp", "tsp", "%", "ppm", "to taste"];

const EMPTY_ROW = () => ({ name: "", amount: "", unit: "g" });

const VOLUME_UNITS = new Set(["cups", "tbsp", "tsp", "mL", "L"]);
const TO_ML = { cups: 236.588, tbsp: 14.787, tsp: 4.929, mL: 1, L: 1000 };

// Each entry: keywords to match against (lowercase), density in g/mL, short hint label
const DENSITY_LOOKUP = [
  { keywords: ["water"],                                    gPerML: 1.000,  hint: "water ~237g/cup" },
  { keywords: ["rye", "rye berr"],                         gPerML: 0.452,  hint: "rye ~107g/cup" },
  { keywords: ["wheat", "wheat berr"],                     gPerML: 0.782,  hint: "wheat ~185g/cup" },
  { keywords: ["oat bran", "oatbran"],                     gPerML: 0.402,  hint: "oat bran ~95g/cup" },
  { keywords: ["oat", "rolled oat"],                       gPerML: 0.338,  hint: "oats ~80g/cup" },
  { keywords: ["vermiculite", "verm"],                     gPerML: 0.507,  hint: "vermiculite ~120g/cup" },
  { keywords: ["coco", "coir"],                            gPerML: 0.360,  hint: "coco coir ~85g/cup" },
  { keywords: ["gypsum"],                                  gPerML: 0.972,  hint: "gypsum ~230g/cup" },
  { keywords: ["bran"],                                    gPerML: 0.402,  hint: "bran ~95g/cup" },
  { keywords: ["millet"],                                  gPerML: 0.780,  hint: "millet ~185g/cup" },
  { keywords: ["corn", "maize"],                           gPerML: 0.720,  hint: "corn ~170g/cup" },
  { keywords: ["perlite"],                                 gPerML: 0.100,  hint: "perlite ~24g/cup" },
  { keywords: ["coffee", "spent coffee", "coffee ground"], gPerML: 0.330,  hint: "coffee grounds ~78g/cup" },
  { keywords: ["sawdust", "wood chip"],                    gPerML: 0.210,  hint: "sawdust ~50g/cup" },
  { keywords: ["straw"],                                   gPerML: 0.070,  hint: "straw ~17g/cup" },
];

// Fuzzy match: returns {gPerML, hint} or null
function lookupDensity(name) {
  if (!name) return null;
  const n = name.toLowerCase().trim();
  // Try longest keyword match first so "oat bran" beats "oat"
  let best = null;
  let bestLen = 0;
  for (const entry of DENSITY_LOOKUP) {
    for (const kw of entry.keywords) {
      if (n.includes(kw) && kw.length > bestLen) {
        best = entry;
        bestLen = kw.length;
      }
    }
  }
  return best;
}

function formatNum(n) {
  if (isNaN(n) || n === null) return "—";
  const abs = Math.abs(n);
  if (abs >= 1000) return Math.round(n).toLocaleString();
  if (abs >= 10)   return parseFloat(n.toFixed(1)).toString();
  return parseFloat(n.toFixed(2)).toString();
}

function toGrams(amount, unit, gPerML) {
  if (!VOLUME_UNITS.has(unit)) return null;
  const n = parseFloat(amount);
  if (isNaN(n) || !gPerML) return null;
  return n * TO_ML[unit] * gPerML;
}

export function parseIngredients(raw) {
  if (!raw) return [EMPTY_ROW()];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) return parsed;
  } catch {
    return [{ name: raw, amount: "", unit: "g" }];
  }
  return [EMPTY_ROW()];
}

export function serializeIngredients(rows) {
  const clean = rows.filter(r => r.name.trim() || r.amount);
  if (clean.length === 0) return null;
  return JSON.stringify(clean);
}

// View-mode table with batch multiplier + auto-detected weight column toggle.
export function IngredientTable({ raw }) {
  const [multiplier, setMultiplier] = useState("1");
  const [showWeight, setShowWeight] = useState(false);

  const rows = (() => {
    if (!raw) return [];
    try {
      const p = JSON.parse(raw);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  })();

  if (rows.length === 0) {
    return <p className="text-sm text-gray-400 italic">No ingredients listed.</p>;
  }

  const mult = parseFloat(multiplier) || 1;
  const scaled = mult !== 1;

  // Only offer weight toggle if at least one row is a volume unit
  const hasVolumeRow = rows.some(r => VOLUME_UNITS.has(r.unit));

  function scaleAmount(amount) {
    const n = parseFloat(amount);
    if (isNaN(n) || amount === "") return amount;
    return formatNum(n * mult);
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {/* Batch scale */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-500">Scale:</span>
          {["0.5", "1", "2", "3"].map(v => (
            <button
              key={v}
              onClick={() => setMultiplier(v)}
              className={`text-xs px-2 py-0.5 rounded border transition-colors ${
                multiplier === v
                  ? "bg-cyan-600 text-white border-cyan-600"
                  : "border-gray-300 text-gray-500 hover:border-cyan-400"
              }`}
            >
              {v}×
            </button>
          ))}
          <input
            type="number"
            value={multiplier}
            onChange={e => setMultiplier(e.target.value)}
            min="0.01"
            step="0.5"
            className="w-14 text-xs border border-gray-300 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-cyan-500"
          />
          <span className="text-xs text-gray-400">×</span>
          {scaled && (
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">scaled</span>
          )}
        </div>

        {/* Weight toggle */}
        {hasVolumeRow && (
          <button
            onClick={() => setShowWeight(w => !w)}
            className={`text-xs px-2.5 py-0.5 rounded-full border font-medium transition-colors ${
              showWeight
                ? "bg-emerald-100 text-emerald-700 border-emerald-300"
                : "border-gray-300 text-gray-400 hover:border-emerald-300 hover:text-emerald-600"
            }`}
          >
            ≈ weight
          </button>
        )}
      </div>

      {/* Table */}
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider pb-1.5 pr-4">Ingredient</th>
            <th className="text-right text-xs font-semibold text-gray-400 uppercase tracking-wider pb-1.5 pr-2">Amount</th>
            <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider pb-1.5">Unit</th>
            {showWeight && (
              <th className="text-right text-xs font-semibold text-gray-400 uppercase tracking-wider pb-1.5 pl-4">≈ Weight</th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const scaledAmt = parseFloat(row.amount) * mult;
            const density = showWeight ? lookupDensity(row.name) : null;
            const weightG = density ? toGrams(scaledAmt.toString(), row.unit, density.gPerML) : null;

            return (
              <tr key={i} className="border-b border-gray-100 last:border-0">
                <td className="py-1.5 pr-4 text-gray-700">{row.name}</td>
                <td className="py-1.5 pr-2 text-right tabular-nums text-gray-700">
                  {scaled && row.amount !== "" && row.unit !== "to taste"
                    ? <span className="text-amber-700 font-medium">{scaleAmount(row.amount)}</span>
                    : row.amount}
                </td>
                <td className="py-1.5 text-gray-500">{row.unit}</td>
                {showWeight && (
                  <td className="py-1.5 pl-4 text-right tabular-nums">
                    {weightG !== null ? (
                      <span className="text-emerald-700 font-medium" title={density.hint}>
                        {formatNum(weightG)} g
                        <span className="text-gray-400 font-normal text-xs ml-1">({density.hint})</span>
                      </span>
                    ) : (
                      <span className="text-gray-300 text-xs">
                        {VOLUME_UNITS.has(row.unit) ? "unknown ingredient" : "—"}
                      </span>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>

      {showWeight && (
        <p className="text-xs text-gray-400 italic">
          Weight estimates are approximate and vary by moisture content and grind.
        </p>
      )}
    </div>
  );
}

// Edit-mode ingredient row editor.
export default function IngredientEditor({ rows, onChange }) {
  function updateRow(i, field, value) {
    const next = rows.map((r, idx) => idx === i ? { ...r, [field]: value } : r);
    onChange(next);
  }

  function addRow() {
    onChange([...rows, EMPTY_ROW()]);
  }

  function removeRow(i) {
    const next = rows.filter((_, idx) => idx !== i);
    onChange(next.length === 0 ? [EMPTY_ROW()] : next);
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-[1fr_100px_90px_28px] gap-2 text-xs font-semibold text-gray-400 uppercase tracking-wider px-1">
        <span>Ingredient</span>
        <span>Amount</span>
        <span>Unit</span>
        <span />
      </div>

      {rows.map((row, i) => (
        <div key={i} className="grid grid-cols-[1fr_100px_90px_28px] gap-2 items-center">
          <input
            type="text"
            value={row.name}
            onChange={e => updateRow(i, "name", e.target.value)}
            placeholder="e.g. Rye berries"
            className="border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />
          <input
            type="number"
            value={row.amount}
            onChange={e => updateRow(i, "amount", e.target.value)}
            placeholder="0"
            min="0"
            step="any"
            className="border border-gray-300 rounded px-2 py-1.5 text-sm text-right focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />
          <select
            value={row.unit}
            onChange={e => updateRow(i, "unit", e.target.value)}
            className="border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
          >
            {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
          <button
            type="button"
            onClick={() => removeRow(i)}
            className="text-gray-300 hover:text-red-400 text-lg leading-none font-light transition-colors"
            aria-label="Remove ingredient"
          >
            ×
          </button>
        </div>
      ))}

      <button
        type="button"
        onClick={addRow}
        className="text-sm text-cyan-600 hover:text-cyan-800 text-left mt-1 font-medium"
      >
        + Add ingredient
      </button>
    </div>
  );
}
